import { api } from './api.js'

const clientFields = new Set([
  'clientId', 'displayName', 'status', 'scopes', 'stationCodes', 'tokenTtlSeconds',
  'secretVersion', 'version', 'createdAt', 'updatedAt', 'clientSecret',
  'clientSecretDisplayedOnce',
])
const allowedScopes = new Set(['pts2.history.read', 'pts2.status.read'])

function text(value, name, maximum) {
  if (typeof value !== 'string' || !value.trim() || value.length > maximum) {
    throw new TypeError(`Invalid Partner API ${name}`)
  }
  return value
}

function strings(value, name, allowed = null) {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string'
    || !item || (allowed && !allowed.has(item))) || new Set(value).size !== value.length) {
    throw new TypeError(`Invalid Partner API ${name}`)
  }
  return [...value]
}

export function mapPartnerApiClient(payload, { secretExpected = false } = {}) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)
    || Object.keys(payload).some((field) => !clientFields.has(field))) {
    throw new TypeError('Invalid Partner API client response')
  }
  const client = {
    clientId: text(payload.clientId, 'clientId', 80),
    displayName: text(payload.displayName, 'displayName', 120),
    status: text(payload.status, 'status', 9),
    scopes: strings(payload.scopes, 'scopes', allowedScopes),
    stationCodes: strings(payload.stationCodes, 'stationCodes'),
    tokenTtlSeconds: Number(payload.tokenTtlSeconds),
    secretVersion: Number(payload.secretVersion),
    version: Number(payload.version),
    createdAt: text(payload.createdAt, 'createdAt', 64),
    updatedAt: text(payload.updatedAt, 'updatedAt', 64),
  }
  if (!['ACTIVE', 'SUSPENDED', 'REVOKED'].includes(client.status)
    || !Number.isInteger(client.tokenTtlSeconds) || client.tokenTtlSeconds < 60
    || client.tokenTtlSeconds > 900 || !Number.isInteger(client.secretVersion)
    || client.secretVersion < 1 || !Number.isInteger(client.version) || client.version < 1) {
    throw new TypeError('Invalid Partner API client response')
  }
  if (secretExpected) {
    client.clientSecret = text(payload.clientSecret, 'clientSecret', 256)
    if (payload.clientSecretDisplayedOnce !== true) throw new TypeError('Invalid one-time secret response')
  } else if ('clientSecret' in payload || 'clientSecretDisplayedOnce' in payload) {
    throw new TypeError('Unexpected Partner API secret')
  }
  return client
}

function mutationPayload(form) {
  return {
    displayName: form.displayName,
    status: form.status,
    scopes: [...form.scopes],
    stationCodes: [...form.stationCodes],
    tokenTtlSeconds: Number(form.tokenTtlSeconds),
    ...(form.version == null ? {} : { version: Number(form.version) }),
  }
}

export async function listPartnerApiClients() {
  const response = await api('/platform/partner-api-clients')
  if (!response || !Array.isArray(response.items) || response.nextCursor != null) {
    throw new TypeError('Invalid Partner API client list')
  }
  return response.items.map((client) => mapPartnerApiClient(client))
}

export const createPartnerApiClient = async (form) => mapPartnerApiClient(await api(
  '/platform/partner-api-clients', { method: 'POST', body: JSON.stringify({
    displayName: form.displayName,
    scopes: [...form.scopes],
    stationCodes: [...form.stationCodes],
    tokenTtlSeconds: Number(form.tokenTtlSeconds),
  }) },
), { secretExpected: true })

export const updatePartnerApiClient = async (clientId, form) => mapPartnerApiClient(await api(
  `/platform/partner-api-clients/${encodeURIComponent(clientId)}`,
  { method: 'PUT', body: JSON.stringify(mutationPayload(form)) },
))

export const rotatePartnerApiClientSecret = async (clientId, version) => mapPartnerApiClient(await api(
  `/platform/partner-api-clients/${encodeURIComponent(clientId)}/secret-rotations`,
  { method: 'POST', body: JSON.stringify({ version: Number(version) }) },
), { secretExpected: true })

export const revokePartnerApiClient = (clientId, version) => api(
  `/platform/partner-api-clients/${encodeURIComponent(clientId)}?version=${encodeURIComponent(version)}`,
  { method: 'DELETE' },
)
